import { Link } from 'react-router-dom';
import { Server, Terminal } from 'lucide-react';
import type { SshHost } from '../hooks/useSshHosts';

function statusDot(status: string) {
  if (status === 'connected') return 'bg-green-500';
  if (status === 'testing') return 'bg-amber-500 animate-pulse';
  if (status === 'error') return 'bg-red-500';
  return 'bg-slate-300';
}

function statusLabel(status: string) {
  if (status === 'connected') return 'Connected';
  if (status === 'testing') return 'Testing...';
  if (status === 'error') return 'Error';
  return 'Disconnected';
}

export default function SshHostCard({ host }: { host: SshHost }) {
  return (
    <Link
      to={`/ssh-hosts/${host.id}`}
      className="group flex flex-col rounded-xl border border-slate-200 bg-white p-5 transition-all hover:border-slate-300 hover:shadow-md"
    >
      <div className="flex items-start justify-between">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100">
          <Server className="h-5 w-5 text-slate-600" />
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`h-2 w-2 rounded-full ${statusDot(host.status)}`} />
          <span className="text-xs text-slate-500">{statusLabel(host.status)}</span>
        </div>
      </div>

      <h3 className="mt-3 font-semibold text-slate-900">{host.name}</h3>
      <p className="mt-0.5 truncate text-xs text-slate-400">
        {host.sshUser}@{host.hostname}:{host.port}
      </p>

      <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
        <Terminal className="h-3 w-3" />
        <span>{host.claudeBinaryPath}</span>
        <span className="text-slate-300">
          {host.authMethod === 'key' ? 'Key auth' : host.authMethod === 'agent' ? 'SSH agent' : 'Password'}
        </span>
      </div>

      {host.lastError && (
        <p className="mt-2 truncate text-xs text-red-400">{host.lastError}</p>
      )}

      <div
        className="mt-auto border-t border-slate-100 pt-3 text-xs text-slate-400"
        style={{ marginTop: 'auto', paddingTop: '0.75rem' }}
      >
        <div className="flex items-center justify-between">
          <span>Max {host.maxConcurrentInstances} instances</span>
          <span>Pool: {host.connectionPoolSize}</span>
        </div>
      </div>
    </Link>
  );
}
